/**
 * Schedule template resolver.
 *
 * A provider_availability row holds three schedules for one (provider, location):
 *   - schedule_template            the weekly pattern in force today
 *   - pending_schedule_template    one scheduled future change (carries its own effective_from)
 *   - schedule_template_history    append-only list of prior patterns, each stamped with the
 *                                  last day it was in force (archived_until)
 *
 * This module is the ONLY place that decides which of the three applies to a date.
 * Every reader keeps its own bound checks (effective_from / effective_until on the
 * resolved template), override handling, holiday handling and is_active filtering.
 *
 * All date comparisons are lexicographic on 'YYYY-MM-DD' strings, matching every
 * existing bound check in the codebase. A backend twin of resolveScheduleTemplate
 * lives inline in the scheduling lambdas as `SCHEDULE_RESOLVER v1`; keep them in step.
 */

import type { DaySchedule, WeeklyScheduleTemplate } from '../types/scheduling';

export const WEEKDAY_KEYS = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface PendingScheduleTemplate {
  effective_from: string;
  monday?: DaySchedule | null;
  tuesday?: DaySchedule | null;
  wednesday?: DaySchedule | null;
  thursday?: DaySchedule | null;
  friday?: DaySchedule | null;
  saturday?: DaySchedule | null;
  sunday?: DaySchedule | null;
}

export interface ScheduleTemplateHistoryEntry {
  template: WeeklyScheduleTemplate;
  /** Last day (YYYY-MM-DD) this template was in force. */
  archived_until: string;
  /** Epoch ms when the entry was written. */
  archived_at?: number;
  reason?: 'promotion' | 'edit';
}

/** The subset of a provider_availability row the resolver reads. */
export interface ProviderAvailabilityLike {
  schedule_template?: WeeklyScheduleTemplate | null;
  pending_schedule_template?: PendingScheduleTemplate | null;
  schedule_template_history?: ScheduleTemplateHistoryEntry[] | null;
}

export function isDateString(value: unknown): value is string {
  return typeof value === 'string' && DATE_RE.test(value);
}

/** True when the object is a usable pending template: a valid start date and at least one weekday key. */
export function isValidPendingTemplate(p: unknown): p is PendingScheduleTemplate {
  if (!p || typeof p !== 'object') return false;
  const obj = p as Record<string, unknown>;
  if (!isDateString(obj.effective_from)) return false;
  return WEEKDAY_KEYS.some(k => Object.prototype.hasOwnProperty.call(obj, k));
}

/**
 * The weekly template in force on `dateStr` for one row: pending when its start
 * date has been reached, else the history entry whose window contains the date,
 * else the current template. Returns null when the row has no template at all.
 */
export function resolveScheduleTemplate(
  row: ProviderAvailabilityLike | null | undefined,
  dateStr: string,
): WeeklyScheduleTemplate | null {
  if (!row) return null;

  const pending = row.pending_schedule_template;
  if (isValidPendingTemplate(pending) && dateStr >= pending.effective_from) {
    return pending as unknown as WeeklyScheduleTemplate;
  }

  const history = Array.isArray(row.schedule_template_history) ? row.schedule_template_history : [];
  for (const entry of history) {
    if (!entry || !entry.template || !isDateString(entry.archived_until)) continue;
    if (dateStr <= entry.archived_until) return entry.template;
  }

  return row.schedule_template ?? null;
}

/** Applies the template's own user-set start/end bounds. */
export function isDateWithinTemplateBounds(
  template: WeeklyScheduleTemplate | null | undefined,
  dateStr: string,
): boolean {
  if (!template) return false;
  if (template.effective_from && dateStr < template.effective_from) return false;
  if (template.effective_until && dateStr > template.effective_until) return false;
  return true;
}

/** Weekday key for a YYYY-MM-DD date, independent of the runtime timezone. */
export function weekdayKeyForDate(dateStr: string): WeekdayKey {
  return WEEKDAY_KEYS[new Date(dateStr + 'T12:00:00Z').getUTCDay()];
}

/**
 * The day schedule in force on `dateStr` after resolving past/current/pending and
 * applying the resolved template's bounds. Null means "closed unless an override opens it".
 */
export function resolveDaySchedule(
  row: ProviderAvailabilityLike | null | undefined,
  dateStr: string,
): DaySchedule | null {
  const template = resolveScheduleTemplate(row, dateStr);
  if (!template || !isDateWithinTemplateBounds(template, dateStr)) return null;
  const day = (template as unknown as Record<string, DaySchedule | null | undefined>)[weekdayKeyForDate(dateStr)];
  return day ?? null;
}

/** Copy of the seven weekday keys only (drops effective_from / effective_until and anything else). */
export function pickWeekdays(template: Partial<WeeklyScheduleTemplate> | null | undefined): Record<WeekdayKey, DaySchedule | null> {
  const out = {} as Record<WeekdayKey, DaySchedule | null>;
  for (const k of WEEKDAY_KEYS) {
    const day = template ? (template as Record<string, DaySchedule | null | undefined>)[k] : null;
    out[k] = day ? { ...day, blocks: Array.isArray(day.blocks) ? day.blocks.map(b => ({ ...b })) : [] } : null;
  }
  return out;
}

/**
 * Compact one-day summary such as "9a-12p, 1p-5p", or null when the day is closed.
 * Shared by the user detail schedule table and the Availability page history list.
 */
export function formatScheduleDay(daySchedule: DaySchedule | null | undefined): string | null {
  if (!daySchedule || !daySchedule.is_available || !daySchedule.blocks?.length) return null;
  const fmt = (t: string) => {
    const [h, m] = t.split(':');
    const hr = parseInt(h, 10);
    const suffix = hr >= 12 ? 'p' : 'a';
    const displayHr = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
    return m === '00' ? `${displayHr}${suffix}` : `${displayHr}:${m}${suffix}`;
  };
  return daySchedule.blocks.map(b => `${fmt(b.start_time)}-${fmt(b.end_time)}`).join(', ');
}
